// DRBFM (Design Review Based on Failure Mode) type definitions

import type { DRBFMReadiness, FunctionPartMatrix } from './drbfm.matrix';
import type { Countermeasure, FailureMode } from './drbfm.results';

// Re-export the split sibling modules so `import { X } from '.../types/drbfm'`
// keeps resolving for every existing caller.
export * from './drbfm.matrix';
export * from './drbfm.case';
export * from './drbfm.results';

// ==============================================================================
// Enums
// ==============================================================================

export type DRBFMStatus = 'draft' | 'analyzing' | 'completed' | 'failed';

export type Severity = 'critical' | 'high' | 'medium' | 'low';

export type CountermeasureType = 'prevention' | 'detection' | 'mitigation';

export type DesignChangeCategory =
  | 'Assembly'
  | 'Boundary'
  | 'Material'
  | 'Dimension'
  | 'Process'
  | 'Structure'
  | 'Surface'
  | 'Tolerance'
  | 'Function'
  | 'Other';

export type ImpactArea =
  | 'Assembly'
  | 'Appearance'
  | 'Design'
  | 'Durability'
  | 'Manufacturing'
  | 'Quality'
  | 'Safety'
  | 'Cost'
  | 'Maintenance';

export type DRBFMCaseStatus =
  | 'draft'
  | 'changes_extracted'
  | 'precheck_ready'
  | 'analyzing'
  | 'completed'
  | 'failed';

export type PrecheckAnswerStatus =
  | 'unanswered'
  | 'answered'
  | 'unknown'
  | 'not_applicable';

export type DRBFMCaseWorkflowStep =
  | 'case-intake'
  | 'change-review'
  | 'precheck'
  | 'analysis'
  | 'results';

export type DRBFMWorkflowPhase =
  | 'requirements_change'
  | 'concept_estimate'
  | 'detailed_design'
  | 'fmea_drbfm'
  | 'post_process_check';

export type DRBFMInputPattern =
  | 'old_new_documents'
  | 'change_report'
  | 'old_new_cad'
  | 'old_new_drawings'
  | 'manual_design_changes'
  | 'function_part_matrix';

export type ChangeGranularity = 'coarse' | 'fine' | 'mixed';

export type DesignChangeReviewStatus =
  | 'ai_candidate'
  | 'needs_info'
  | 'accepted'
  | 'modified'
  | 'rejected'
  | 'deferred'
  | 'superseded';

export type ScoreStatus = 'unscored' | 'provisional' | 'reviewed' | 'legacy_provisional';
export type CountermeasureRequirement = 'pending' | 'required' | 'not_required';
export type ContentReadiness = 'draft' | 'content_reviewed' | 'scored';

export type DRBFMStage =
  | 'baseline_candidates'
  | 'extract_changes'
  | 'normalize_changes'
  | 'build_function_part_matrix'
  | 'prefill_function_part_matrix'
  | 'promote_matrix_changes'
  | 'review_changes'
  | 'search_knowledge'
  | 'generate_failure_modes'
  | 'generate_countermeasures'
  | 'link_cad'
  | 'attach_drawing_evidence'
  | 'export';

// ==============================================================================
// Domain Models
// ==============================================================================

export interface FlowProgress {
  flow1?: 'pending' | 'in_progress' | 'completed' | 'failed';
  flow2?: 'pending' | 'in_progress' | 'completed' | 'failed';
  flow3?: 'pending' | 'in_progress' | 'completed' | 'failed';
  flow4?: 'pending' | 'in_progress' | 'completed' | 'failed';
  flow5?: 'pending' | 'in_progress' | 'completed' | 'failed';
}

export interface DRBFMSession {
  id: string;
  tenantId: string;
  userId: string;
  itemName: string;
  title?: string;
  specOldDocumentId?: string;
  specNewDocumentId?: string;
  workflowPhase?: DRBFMWorkflowPhase;
  inputPattern?: DRBFMInputPattern;
  changeGranularity?: ChangeGranularity;
  workflowMetadata?: Record<string, unknown>;
  status: DRBFMStatus;
  currentFlow: number;
  flowProgress: FlowProgress;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  htmlExportPath?: string;
}

export interface DesignChange {
  id: string;
  sessionId?: string;
  category: DesignChangeCategory;
  itemName: string;
  oldSpecification?: string;
  newSpecification?: string;
  changeDescription?: string;
  impactAreas: ImpactArea[];
  sourceArtifactId?: string | null;
  reviewStatus?: DesignChangeReviewStatus;
  granularity?: ChangeGranularity | null;
  confidence?: number | null;
  reviewerNote?: string | null;
  changeMetadata?: Record<string, unknown>;
  canonicalKey?: string | null;
  generationEligible?: boolean;
  sourceEvidence?: Record<string, unknown>[];
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  createdAt?: string;
}

export interface DRBFMWorkflowArtifact {
  id: string;
  tenantId?: string;
  sessionId?: string;
  artifactType: string;
  sourceType: string;
  status?: string;
  documentId?: string | null;
  cadModelId?: string | null;
  title?: string | null;
  payload: Record<string, unknown>;
  createdBy?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface DRBFMStageRun {
  id: string;
  tenantId?: string;
  sessionId?: string;
  stage: DRBFMStage | string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | string;
  inputArtifactIds: string[];
  outputArtifactIds: string[];
  startedAt?: string | null;
  completedAt?: string | null;
  errorMessage?: string | null;
  runMetadata: Record<string, unknown>;
  createdBy?: string | null;
  createdAt?: string | null;
}

export interface DRBFMWorkflowResponse {
  session: DRBFMSession;
  artifacts: DRBFMWorkflowArtifact[];
  stageRuns: DRBFMStageRun[];
  designChanges: DesignChange[];
  failureModes: FailureMode[];
  countermeasures: Countermeasure[];
  decisionCards: Record<string, unknown>[];
  readiness: DRBFMReadiness;
  functionPartMatrix?: FunctionPartMatrix | null;
}

// ==============================================================================
// Utility Functions (Type Guards)
// ==============================================================================

export function isSeverityCritical(severity: Severity): boolean {
  return severity === 'critical';
}

export function isSeverityHigh(severity: Severity): boolean {
  return severity === 'critical' || severity === 'high';
}

export function getSeverityColor(severity: Severity): string {
  switch (severity) {
    case 'critical':
      return '#d32f2f'; // red
    case 'high':
      return '#f57c00'; // orange
    case 'medium':
      return '#fbc02d'; // yellow
    case 'low':
      return '#388e3c'; // green
    default:
      return '#757575'; // grey
  }
}

export function getCountermeasureTypeColor(type: CountermeasureType): string {
  switch (type) {
    case 'prevention':
      return '#2e7d32'; // green
    case 'detection':
      return '#1565c0'; // blue
    case 'mitigation':
      return '#e65100'; // orange
    default:
      return '#757575'; // grey
  }
}

export function getCountermeasureTypeLabel(type: CountermeasureType): string {
  switch (type) {
    case 'prevention':
      return '予防';
    case 'detection':
      return '検出';
    case 'mitigation':
      return '軽減';
    default:
      return type;
  }
}

export function getCategoryColor(category: DesignChangeCategory): string {
  switch (category) {
    case 'Material':
      return '#1565c0';
    case 'Dimension':
      return '#c2185b';
    case 'Process':
      return '#2e7d32';
    case 'Structure':
      return '#e65100';
    case 'Function':
      return '#7b1fa2';
    default:
      return '#546e7a';
  }
}
